import { useEffect, useState, useCallback } from "react";
import { Bell, Check } from "@phosphor-icons/react";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { ScrollArea } from "./ui/scroll-area";
import client from "../lib/api";

const KIND_LABEL = {
  booking: "New booking",
  message: "Message",
  assignment: "Assignment",
  backup: "Backup",
  released: "Open pool",
  deadline: "Deadline",
  lead: "Lead",
  review: "Review",
  deliverables: "Deliverables",
  payout: "Payout",
};

function timeAgo(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  if (s < 604800) return `${Math.floor(s / 86400)}d ago`;
  return d.toLocaleDateString();
}

export default function NotificationBell({ onNavigate }) {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await client.get("/notifications");
      setItems(data.items || []);
      setUnread(data.unread || 0);
    } catch {}
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  const markAll = async () => {
    try {
      await client.post("/notifications/read-all");
      setItems((prev) => prev.map((n) => ({ ...n, read: true })));
      setUnread(0);
    } catch {}
  };

  const clickItem = async (n) => {
    if (!n.read) {
      setItems((prev) => prev.map((x) => (x.id === n.id ? { ...x, read: true } : x)));
      setUnread((u) => Math.max(0, u - 1));
      try { await client.post(`/notifications/${n.id}/read`); } catch {}
    }
    if (onNavigate) {
      setOpen(false);
      onNavigate(n);
    }
  };

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (o) load(); }}>
      <PopoverTrigger asChild>
        <button
          data-testid="notification-bell"
          className="relative flex h-10 w-10 items-center justify-center rounded-full border border-white/20 bg-white/10 text-[#FBFAF7] transition-colors hover:bg-white/20 hover:border-white/30"
          aria-label="Notifications"
        >
          <Bell size={20} weight={unread > 0 ? "fill" : "regular"} />
          {unread > 0 && (
            <span
              data-testid="notification-badge"
              className="absolute -top-0.5 -right-0.5 flex h-5 min-w-5 items-center justify-center rounded-full bg-[#B08D57] px-1 text-[10px] font-bold text-[#1A1715] ring-2 ring-[#4A0000]"
            >
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[360px] p-0 bg-[#4A0000] text-[#FBFAF7] border-[#B08D57]/30 shadow-premium-lg" data-testid="notification-panel">
        <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
          <div>
            <p className="eyebrow text-[#D8B978] leading-none">Notifications</p>
            <p className="text-sm font-semibold text-[#FBFAF7] mt-1">{unread > 0 ? `${unread} unread` : "You're all caught up"}</p>
          </div>
          {unread > 0 && (
            <button data-testid="notification-mark-all" onClick={markAll}
              className="flex items-center gap-1 text-xs font-medium text-[#D8B978] hover:underline">
              <Check size={13} weight="bold" /> Mark all read
            </button>
          )}
        </div>
        <div className="max-h-[min(70vh,420px)] overflow-y-auto overscroll-contain" data-testid="notification-scroll">
          {items.length === 0 ? (
            <div className="px-4 py-10 text-center text-sm text-white/45" data-testid="notification-empty">
              No notifications yet
            </div>
          ) : (
            <ul>
              {items.map((n) => (
                <li key={n.id}>
                  <button
                    data-testid={`notification-item-${n.id}`}
                    onClick={() => clickItem(n)}
                    className={`w-full text-left px-4 py-3 border-b border-white/[0.08] transition-colors hover:bg-white/[0.06] ${n.read ? "" : "bg-[#B08D57]/[0.12]"}`}
                  >
                    <div className="flex items-center gap-2">
                      {!n.read && <span className="h-1.5 w-1.5 rounded-full bg-[#D8B978] shrink-0" />}
                      <span className="text-[10px] uppercase tracking-wide font-semibold text-[#D8B978]">{KIND_LABEL[n.kind] || n.kind}</span>
                      <span className="ml-auto text-[11px] text-white/40 shrink-0">{timeAgo(n.created_at)}</span>
                    </div>
                    <p className="text-sm font-medium text-[#FBFAF7] mt-0.5 leading-snug">{n.title}</p>
                    {n.body && <p className="text-xs text-white/60 mt-0.5 leading-snug line-clamp-2">{n.body}</p>}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
